/**
 * Artikelpositionen der Anlagemaske - die Struktur des Bestands (`renderNewOrderItems`,
 * `index.html:7984-8017`) mit der JTL-Artikelsuche der Spec ("Bewusste Verhaltensaenderungen",
 * Nr. 3) in der Artikelzelle. Menge und EK bleiben frei editierbare Zahlenfelder wie im Bestand -
 * Verhaltensaenderung 3 betrifft ausdruecklich nur "Artikel und Lieferanten".
 *
 * ================= ANGEGLICHEN AM 2026-08-15 =================
 *
 * Bis dahin zeichnete diese Flaeche ein oberes Suchfeld und darunter eine nackte Tabelle: kein
 * Kartenrahmen, kein Sortierhinweis, kein Ziehgriff, keine Spaltenbreiten, "Preis" statt "EK / Stk.",
 * die Kennzeichen als unbeschriftete Kaestchen - und keinen Weg, eine Zeile von Hand anzulegen. Das
 * ist jetzt die Struktur des Bestands; das CSS dafuer lag laengst ungenutzt im Baum
 * (`styles/bestand.css:340-358`, `.new-order-excel-editor` ff.).
 *
 * Das obere Suchfeld ist dabei ENTFALLEN: der Artikel wird in der Zeile gewaehlt, und zwei Wege fuer
 * dieselbe Sache waeren einer zu viel.
 *
 * NICHT `JtlSuchfeld` in der Zelle, obwohl die Anlagemaske es fuer den Lieferanten benutzt:
 * `styles/neubau/suchfeld.css:49-51` haelt ausdruecklich fest, dass die Tabellenzelle die Huelle
 * `.jtl-suchfeld` NICHT bekommt - deren 34px-Einzug fuer die Lupe sprengt eine 180px-Spalte. Hier
 * steht deshalb dasselbe Zellen-Markup wie im Bestell-Reiter (`Drawer/MainTab/Positionen.tsx`):
 * blankes `<input>` plus `.jtl-treffer`-Liste. Die vier Eigenschaften aus Constraint 16 (Debounce
 * 300 ms, Mindestlaenge 2, Cache, Single-Flight) kommen von `jtlSuche` unten, nicht von der Huelle.
 *
 * ================= DER BESTELLEXCEL-IMPORT (Task 3, Plan 2026-08-17-bestellexcel-anlagemaske) =====
 *
 * `chooseNewOrderExcel`/`readNewOrderExcel` (`index.html:8052-8088`) sind jetzt verdrahtet, rechts in
 * `.new-order-excel-toolbar` (vorher LEER, siehe die Vorgaengerfassung dieses Kommentars). Geladen wird
 * ausschliesslich ueber `ladeParser()` (`lib/parsers/naht.ts`), NIE mit einem statischen Import von
 * `auslesen.ts`/`xlsx` (Global Constraint 3) - `xlsx` darf nicht im Hauptbuendel liegen
 * (`test/reference/bundle.test.ts`).
 *
 * Erfolg ERSETZT die Positionen (`index.html:8076`), ergaenzt sie nicht - eine zweite Bestellexcel-Wahl
 * verwirft die erste Auswahl vollstaendig, wie im Bestand. Die Datei UND der volle Entwurf gehen per
 * `onExcelDatei` einen Rang hoeher (Anlagemaske) - NICHT ueber Modulzustand hier, weil
 * `neueBestellungAnlegen` (`App.tsx`) die Datei erst nach dem erfolgreichen Anlegen als Beleg ablegt
 * (Teil 2b des Task-Briefs) und den Entwurf danach als `orderExcelImport` schreibt (Task 4).
 *
 * KEIN Schreibversuch HIER auf `orderExcelImport`: die Spalte liegt zwar seit Task 4 in der Feldgruppe
 * `extras` und ist damit ueber `PATCH /api/orders/:id/extras` schreibbar - aber diese Komponente hat
 * (anders als `neueBestellungAnlegen`) keine `orderId`, gegen die sie schreiben koennte: die Bestellung
 * entsteht in JTL erst beim Absenden der Maske. Der volle Entwurf (`ExcelEntwurf` - Warnungen,
 * Blattname, Zeilenzahl, `extractedAt`) wird deshalb weiterhin lokal gehalten (`excelZustand`, fuer die
 * Statuszeile) UND zusaetzlich unveraendert an `onExcelDatei` gereicht - `App.tsx#neueBestellungAnlegen`
 * ergaenzt ihn dort um die tatsaechlich uebernommenen Positionen und `appliedAt` und schreibt ihn nach
 * dem Beleg-Upload.
 *
 * Die Statuszeile (`.new-order-excel-state`) ist die Fassung von `renderNewOrderExcelState`
 * (`index.html:7962-7982`), im Kern uebernommen (busy/Fehler/bereit mit Quelle · Blatt ·
 * n Artikelposition(en) erkannt · Warnungen).
 *
 * Die Spaltentrenner fehlen hier ganz, und das ist KEINE Luecke: `renderNewOrderItems` (`:8012`)
 * baut seine Kopfzeile ohne `itemTableHeaders`, also auch im Bestand ohne Trenner - nur die
 * Spaltenbreiten aus `itemTableColgroup` gelten.
 *
 * **Das Ziehen der Positionen** war bis zum 2026-08-18 dieselbe benannte Grenze wie im
 * Bestell-Reiter (`Drawer/MainTab/Positionen.tsx`, Kopfkommentar): der Griff war gezeichnet, aber
 * nicht verdrahtet. Die Handler stehen jetzt in `PositionenZiehen.ts` (Kopfkommentar dort nennt
 * die `index.html`-Herkunft und die zwei bewussten Abweichungen: kein Toast, kein gemeinsames
 * Modul mit dem Drawer).
 */
import { useMemo, useRef, useState, type JSX } from "react";
import { ITEM_TABLE_COLUMNS } from "../../config/board.ts";
import { jtlSuche } from "../../api/jtlSuche.ts";
import { Ikone } from "../Ikone.tsx";
import { Artikelbild } from "../Artikelbild.tsx";
import { verknuepftMarke } from "../VerknuepftMarke.tsx";
import { artikelKennung } from "../../lib/artikelbild.ts";
import { istManuellerArtikel } from "../../lib/manuelleArtikel.ts";
import { fmtMoney } from "../../lib/money.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import { ladeParser } from "../../lib/parsers/naht.ts";
import { positionenGegenJtlAbgleichen } from "./JtlAutoLink.ts";
import {
  neuePositionenZiehenRef,
  positionAbgelegt,
  positionTaste,
  positionZielFokussieren,
  positionZielUeberfahren,
  positionZiehenEnde,
  positionZiehenStart,
} from "./PositionenZiehen.ts";
import type { JtlTreffer } from "./JtlSuchfeld.tsx";
import type { OrderItem } from "../../lib/types.ts";
import type { ExcelEntwurf, ExcelZeile } from "../../lib/parsers/naht.ts";

export interface JtlArtikelTreffer extends JtlTreffer {
  sku?: unknown;
  name?: unknown;
  price?: unknown;
  /**
   * Steuersatz des Standard-Lieferantenartikels (`tliefartikel.fMwSt`) - seit dem 2026-08-15 Teil
   * des Treffers, weil die Anlagemaske direkt nach JTL schreibt und
   * `spLieferantenBestellungPosErstellen` ihn je Position verlangt.
   */
  mwstProzent?: unknown;
  /**
   * Ob der Artikel in JTL ein Bild hat (`GET /api/jtl/articles`, seit Auftrag "hatBild vorab
   * mitgeben", 2026-08-27) - `App.tsx#anlagemaskeArtikelSuche` reicht das Feld explizit durch (die
   * Abbildung dort baut ein NEUES Objektliteral, kein roher Durchreich wie bei `JtlArtikel`).
   */
  hatBild?: unknown;
  /**
   * Ob der Artikel mit dem gewaehlten Lieferanten verknuepft ist (`GET /api/jtl/articles?kLieferant=`,
   * Auftrag "Verknuepfungsstatus in der Anlagemaske", 2026-08-28) - roh vom Aufrufer (gleiche
   * Konvention wie `hatBild` oben), eingeordnet wird beim Lesen (`verknuepftMarke`, `uebernehmen`
   * unten).
   */
  verknuepft?: unknown;
}

export interface PositionsEditorProps {
  artikelSuche: (begriff: string) => Promise<JtlArtikelTreffer[]>;
  /**
   * DIESELBE Suche wie `artikelSuche`, aber OHNE die Debounce-Drossel `jtlSuche(...)` (Zeile ~304,
   * `gedrosselt`) - Fund beim Bugreport "JTL-Autoabgleich verknuepft nur einen Bruchteil der SKUs"
   * (2026-09-04, siehe `JtlAutoLink.ts`-Kopfkommentar): `excelDateiVerarbeiten` unten ruft ueber
   * `positionenGegenJtlAbgleichen` bis zu `MAX_GLEICHZEITIGE_SUCHEN = 4` gleichzeitige, VERSCHIEDENE
   * SKUs ab. `artikelSuche` ist dagegen eine gedrosselte `jtlSuche(...)`-Instanz mit EINEM geteilten
   * Debounce-Timer je Aufrufer (`api/jtlSuche.ts`) - mehrere gleichzeitige, verschiedene
   * Suchbegriffe kollidieren dort im selben Zeitfenster, und nur der ZULETZT eingereihte wird
   * tatsaechlich abgefragt; alle anderen Aufrufer (auch fuer andere SKUs) bekommen faelschlich genau
   * dessen Ergebnis. `excelDateiVerarbeiten` nutzt deshalb `artikelSucheRoh`, NICHT `artikelSuche` -
   * die interaktive Zeilensuche (`suchen()` unten, `gedrosselt`) bleibt unveraendert bei
   * `artikelSuche`.
   */
  artikelSucheRoh: (begriff: string) => Promise<JtlArtikelTreffer[]>;
  positionen: readonly OrderItem[];
  onAendern: (positionen: OrderItem[]) => void;
  /**
   * Die Bestellwaehrung - "EUR" oder "USD", wie sie der ausgewaehlte Lieferant vorgibt
   * (`tlieferant.cWaehrungISO`, im Suchtreffer als `eur`). Pflicht und nicht optional mit Vorgabe
   * "EUR": genau diese stille Vorgabe war der Fehler, den diese Komponente bis zum 2026-08-16
   * hatte (siehe Kommentar ueber `ITEM_REORDER_COLUMN_WIDTH`). Wer sie einbindet, soll die Frage
   * beantworten muessen.
   */
  waehrung: "EUR" | "USD";
  /**
   * Die gewaehlte Bestellexcel-Datei SAMT dem vollen Auswertungsentwurf, EINEN Rang hoeher gereicht -
   * `neueBestellungAnlegen` (`App.tsx`) legt die Datei erst NACH dem Anlegen als Beleg ab (Teil 2b des
   * Task-Briefs, `dokumentHochladen` verlangt eine bestehende `orderId`) und schreibt danach `entwurf`
   * (ergaenzt um die tatsaechlich uebernommenen Positionen und `appliedAt`) als `orderExcelImport`
   * (Task 4). Ueber einen eigenen Rueckruf statt Modulzustand, damit dieser Editor bei einer erneuten
   * Auswahl (die die Positionen ERSETZT) austauschbar bleibt.
   */
  onExcelDatei: (datei: File, entwurf: ExcelEntwurf) => void;
  /**
   * Meldet, ob GERADE eine Bestellexcel ausgelesen wird - Befund der Pruefung von `a434ea7`
   * (Ruling, wichtiger Befund 1): `index.html:8104-8107` blockt "Bestellung speichern", solange
   * `newOrderExcelBusy` steht ("Bitte warten, bis die Bestellexcel vollständig ausgelesen wurde.").
   * Der busy-Zustand lebt HIER im Editor (`excelZustand`); ohne diesen Rueckruf kennt ihn die
   * Anlagemaske nicht und der Riegel liesse sich dort nicht nachbauen. Derselbe Rang-hoeher-Weg wie
   * `onExcelDatei` - kein Modulzustand.
   */
  onExcelLadenAendern: (laedt: boolean) => void;
}

/** `readNewOrderExcel` (index.html:8062): dieselbe Endungspruefung, wortgleich. */
const EXCEL_DATEIENDUNG = /\.(xlsx|xls|xlsm|ods|csv)$/i;

/**
 * Der sichtbare Zustand der Bestellexcel-Statuszeile (`.new-order-excel-state`) - eigene, schlichte
 * Fassung von `renderNewOrderExcelState` (`index.html:7962-7982`): busy/Fehler/bereit, je mit dem
 * Text, den der Bestand an dieser Stelle zeigt.
 */
type ExcelZustand =
  | { art: "leer" }
  | { art: "busy" }
  | { art: "fehler"; meldung: string }
  | { art: "bereit"; quelle: string; blatt: string; warnungen: readonly string[] };

/** `blankOrderItem(item)` (index.html:7833) auf eine Bestellexcel-Zeile angewandt - `leerePosition()`
 *  bleibt die Basis (foc/manual/kArtikel unangetastet), die vier Excel-Felder ueberschreiben sie. OHNE
 *  `kArtikel`: die Zeile muss - wie im Bestand - noch gegen die JTL-Artikelsuche bestaetigt werden,
 *  bevor `Anlagemaske#absenden` sie annimmt (Verhaltensaenderung 3). */
function positionAusExcelZeile(zeile: ExcelZeile): OrderItem {
  return {
    ...leerePosition(),
    name: zeile.name ?? "",
    sku: zeile.sku ?? "",
    ean: zeile.ean ?? "",
    qty: zeile.qty ?? "",
    price: zeile.price ?? "",
  };
}

/**
 * `index.html:4633-4634` - die beiden Spalten ohne Ueberschrift.
 *
 * DOPPELT zu `Drawer/MainTab/Positionen.tsx`, und das ist hier die kleinere Schuld: im Bestand sind
 * `itemTableWidth`/`itemTableColgroup` (`index.html:6790-6799`) globale Funktionen, die sich BEIDE
 * Tabellen teilen. Ein gemeinsames Modul ist faellig, sobald beide Seiten stillstehen -
 * `Positionen.tsx` liegt derzeit geaendert im Baum.
 */
const ITEM_REORDER_COLUMN_WIDTH = 34;
const ITEM_ACTION_COLUMN_WIDTH = 30;
/**
 * Auftrag 2 - dritte kopflose Spalte, genauso eingebaut wie in `Drawer/MainTab/Positionen.tsx`
 * (siehe dort). Absichtlich EIN drittes Mal dupliziert statt die Breitenlogik zusammenzufuehren -
 * siehe Kopfkommentar dieser Datei ("DOPPELT zu Drawer/MainTab/Positionen.tsx").
 */
const ITEM_IMAGE_COLUMN_WIDTH = 36;

/** `index.html:4676` - arbeitsplatzlokale Einstellung, wie im Bestand ueber `localStorage`. */
function spaltenbreitenLesen(): Record<string, number> {
  try {
    const roh = localStorage.getItem("hubItemTableWidths");
    if (!roh) return {};
    const wert = JSON.parse(roh);
    return wert && typeof wert === "object" ? (wert as Record<string, number>) : {};
  } catch { // still: wie im Bestand (index.html:4676) darf ein blockiertes oder kaputtes localStorage die Maske nicht verhindern - es gelten dann die Standardbreiten
    return {};
  }
}

/** `index.html:6790` */
function itemTableColumnWidth(
  spalte: (typeof ITEM_TABLE_COLUMNS)[number],
  breiten: Record<string, number>,
): number {
  return Math.max(spalte.min, Math.min(spalte.max, Number(breiten[spalte.key]) || spalte.width));
}

/** `index.html:6794` */
function itemTableWidth(breiten: Record<string, number>): number {
  return (
    ITEM_TABLE_COLUMNS.reduce((summe, spalte) => summe + itemTableColumnWidth(spalte, breiten), 0) +
    ITEM_REORDER_COLUMN_WIDTH +
    ITEM_IMAGE_COLUMN_WIDTH +
    ITEM_ACTION_COLUMN_WIDTH
  );
}

/** `blankOrderItem()` (index.html:7833), auf die Felder dieser Maske beschraenkt. */
function leerePosition(): OrderItem {
  return {
    name: "",
    sku: "",
    qty: "",
    price: "",
    foc: false,
    manual: false,
    /**
     * DIE JTL-ARTIKELNUMMER. `null` bei einer von Hand angelegten Zeile ohne Treffer - die kann
     * nicht nach JTL: `pruefeNeueBestellung` (`backend/src/jtl/schreiben/anlegen.ts:91`) weist jede
     * Position ohne ganzzahliges `kArtikel >= 1` als FREIPOSITION zurueck (Spec Zeile 573-577).
     * Die Anlagemaske faengt das vor dem Absenden ab, statt eine Artikelnummer zu erfinden.
     */
    kArtikel: null,
    mwstProzent: null,
  };
}

/** `(Number(item.qty) || 0) * (Number(item.price) || 0)` - wie `renderNewOrderItems` (index.html:7991). */
function positionsSumme(item: OrderItem): number {
  return (Number(item.qty) || 0) * (Number(item.price) || 0);
}

/**
 * ============ Der Waehrungsfehler (2026-08-16) ============
 *
 * Bis hierher stand hier ein fest verdrahtetes
 * `new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" })` - eine lokale Kopie der
 * HALBEN Formel aus `lib/money.ts`, die die Bestellwaehrung nicht kannte. Die Maske zeigte deshalb
 * "15,00 €", waehrend der Beleg daneben korrekt in USD entstand (`App.tsx`:
 * `waehrung: daten.eur ? "EUR" : "USD"`, gespeist aus `tlieferant.cWaehrungISO`). Bei 24 der 50
 * Lieferanten stand am Warenwert das falsche Zeichen.
 *
 * Ersetzt durch `fmtMoney` (`lib/money.ts`) - dieselbe Quelle, aus der Board, Drawer und
 * Sammellieferung ihre Betraege beziehen; die Begruendung gegen eine zweite lokale Kopie steht im
 * Kopfkommentar von `SammellieferungDialog.tsx`.
 *
 * WARUM EIN OBJEKTLITERAL STATT EINER `Order`: die Maske hat noch keine Bestellung (Task 23 - die
 * Erzeugung liegt beim Aufrufer), und `fmtMoney` braucht auch keine. `Order` ist ein OFFENER
 * Datensatz (`{ [feld: string]: unknown }`, `lib/types.ts`), und `orderCurrency` liest als erstes
 * `o.waehrung` und gibt es zurueck, sobald es "EUR" oder "USD" ist - die Lieferantenliste wird
 * dann gar nicht mehr befragt, deshalb `null`. Das Literal traegt genau das eine Feld, das hier
 * bekannt ist.
 *
 * Die naheliegende Alternative - eine Hilfsfunktion `fmtBetrag(v, waehrung)` in `lib/money.ts` -
 * scheidet aus: `money.ts` steht in `MODULE` (`scripts/gen-herkunft.ts`), und dort verlangt jedes
 * exportierte Symbol eine Herkunftsmarke `index.html:NNNN`, deren Zeilenfenster den gleichen
 * Bezeichner enthaelt. Ein neu erfundener Name kann dort nicht wohnen (`test/reference/
 * herkunft.test.ts` wird rot) - und den Waechter zu umgehen, statt ihn zu bedienen, macht die
 * naechste stille Luecke unsichtbar.
 */

/** Die offene Trefferliste GENAU EINER Zeile - samt des Textes, der sie ausgeloest hat. */
interface Zeilensuche {
  zeile: number;
  /**
   * Der getippte Text. Er steht im Feld ANSTELLE des Artikelnamens, solange gesucht wird - ohne ihn
   * waere das Feld ein kontrolliertes `value={item.name}`, in dem Tippen nichts bewegt.
   */
  text: string;
  liste: JtlArtikelTreffer[];
  fehler: string | null;
}

export function PositionsEditor({
  artikelSuche,
  artikelSucheRoh,
  positionen,
  onAendern,
  waehrung,
  onExcelDatei,
  onExcelLadenAendern,
}: PositionsEditorProps): JSX.Element {
  const [suche, setSuche] = useState<Zeilensuche | null>(null);
  const [breiten] = useState<Record<string, number>>(() => spaltenbreitenLesen());
  const [excelZustand, setExcelZustand] = useState<ExcelZustand>({ art: "leer" });
  const excelInputRef = useRef<HTMLInputElement>(null);
  /** Traegt den Ziehzustand ueber dragstart -> dragover -> drop/dragend hinweg (`PositionenZiehen.ts`). */
  const ziehenRef = useRef(neuePositionenZiehenRef());

  /**
   * EINE Drossel fuer alle Zeilen (Constraint 16). Sie haelt Cache und Debounce-Uhr in ihrem
   * Closure; es wird immer nur in einer Zeile getippt, deshalb genuegt eine.
   */
  const gedrosselt = useMemo(
    () => jtlSuche<JtlArtikelTreffer>({ abruf: artikelSuche }),
    [artikelSuche],
  );

  function hinzufuegen(): void {
    onAendern([...positionen, leerePosition()]);
  }

  /** `chooseNewOrderExcel` (index.html:8052-8058): Wert VOR dem Oeffnen leeren, sonst loest eine
   *  zweite Auswahl DERSELBEN Datei kein `change` aus. */
  function excelDateiWaehlen(): void {
    if (excelZustand.art === "busy") return;
    if (excelInputRef.current) excelInputRef.current.value = "";
    excelInputRef.current?.click();
  }

  /**
   * `readNewOrderExcel` (index.html:8060-8088): Endungspruefung, dann `ladeParser()` (Global
   * Constraint 3 - NIE ein statischer Import), dann `excelEntwurfAuslesen`. Erfolg ERSETZT die
   * Positionen (`:8076`) und reicht die Datei ueber `onExcelDatei` nach oben (Teil 2b). Fehlschlag
   * zeigt den Text aus `fehlertext()` - der Satz "Bestellexcel konnte nicht ausgelesen werden." als Kontext
   * (der Rueckfalltext des Bestands, `:8081`), dahinter die Ursache statt `error.message` - sichtbar in der
   * Statuszeile, nicht nur in der Konsole.
   *
   * SEIT dem Auftrag "Automatische SKU-Verknuepfung" (`JtlAutoLink.ts`): zwischen dem Bau der rohen
   * Positionsliste und `onAendern` steht jetzt `positionenGegenJtlAbgleichen` - jede Zeile mit einer
   * SKU wird gegen JTL geprueft, ein exakter Treffer bestaetigt die Zeile automatisch (wie eine
   * manuelle Auswahl), sonst bekommt sie `jtlKeinTreffer: true` (Markierung `item-jtl-unmatched`
   * unten in der Tabelle). Ist die Suche fuer ihre SKU gescheitert, bekommt sie `jtlSucheFehler` statt
   * `jtlKeinTreffer` (Marke "JTL-Suche gescheitert" samt Ursache, Klasse `item-jtl-suche-gescheitert`).
   */
  async function excelDateiVerarbeiten(datei: File | undefined): Promise<void> {
    if (!datei) return;
    if (!EXCEL_DATEIENDUNG.test(datei.name)) {
      setExcelZustand({ art: "fehler", meldung: "Bitte eine Excel-, ODS- oder CSV-Datei auswählen." });
      return;
    }
    setExcelZustand({ art: "busy" });
    onExcelLadenAendern(true);
    try {
      const parser = await ladeParser();
      const entwurf = await parser.excelEntwurfAuslesen(datei);
      const positionenRoh = entwurf.items.map(positionAusExcelZeile);
      const positionenAbgeglichen = await positionenGegenJtlAbgleichen(positionenRoh, artikelSucheRoh);
      onAendern(positionenAbgeglichen);
      onExcelDatei(datei, entwurf);
      setExcelZustand({
        art: "bereit",
        quelle: entwurf.sourceName || datei.name,
        blatt: entwurf.sheetName || "Tabelle 1",
        warnungen: entwurf.warnings,
      });
    } catch (fehler) {
      // Die Statuszeile gab es schon (Spec R2, "Inline bleibt inline"): neu ist der Text. Der feste Satz
      // wird zum Kontext, die Ursache kommt aus `fehlertext()` - ein gescheitertes Nachladen des
      // xlsx-Stuecks (`TypeError: Failed to fetch dynamically imported module`) heisst dort "Die Anwendung
      // wurde aktualisiert. Bitte die Seite neu laden".
      // Die Parser (`lib/parsers`) werfen ihre deutschen Saetze fuer den Nutzer ("Die Bestellexcel enthaelt
      // keine lesbaren Daten", "Die Datei liess sich nicht lesen ...") als `NutzerFehler`; `fehlertext()` gibt
      // sie unveraendert aus. Jeder gewoehnliche `Error` (auch einer aus SheetJS) ist ein Programmfehler und
      // ergibt den Sammeltext - sein englischer Wortlaut gehoert in die Konsole, nicht in die Zeile.
      const kontext = "Bestellexcel konnte nicht ausgelesen werden.";
      setExcelZustand({ art: "fehler", meldung: fehlertext(fehler, kontext) ?? kontext });
    } finally {
      onExcelLadenAendern(false);
    }
  }

  /** Verhaltensaenderung 3: Tippen sucht, es schreibt nicht in die Position. */
  function suchen(index: number, text: string): void {
    setSuche({ zeile: index, text, liste: [], fehler: null });
    /**
     * MIT Ablehnungszweig - `JtlSuchfeld` hat denselben Befund am 2026-08-13 abgestellt: ein
     * `.then(...)` ohne zweiten Zweig macht aus einem Suchfehler eine unbehandelte Zurueckweisung,
     * die der Bediener nie zu sehen bekommt.
     *
     * Die Wache `s.zeile === index && s.text === text` verwirft ueberholte Antworten: der Debounce
     * loest alle im Fenster aufgelaufenen Versprechen mit DEMSELBEN Ergebnis auf, und ohne sie
     * schriebe die Antwort auf "al" noch in eine Zeile, die inzwischen "alk" heisst.
     */
    void gedrosselt(text).then(
      (liste) =>
        setSuche((s) => (s && s.zeile === index && s.text === text ? { ...s, liste } : s)),
      (fehler: unknown) =>
        setSuche((s) =>
          s && s.zeile === index && s.text === text
            ? {
                ...s,
                liste: [],
                fehler: fehlertext(fehler, "Suche fehlgeschlagen."),
              }
            : s,
        ),
    );
  }

  /**
   * Der gewaehlte Treffer fuellt Bezeichnung, SKU, EK und die beiden JTL-Angaben - und schlaegt bei
   * einer Artikelnummer mit `x`-Praefix das Kennzeichen M vor (Spec Zeile 644, „Abgeleitete
   * Klassifikationen"; die Regel selbst steht in `lib/manuelleArtikel.ts`).
   *
   * VORSCHLAG, kein Zwang: das Kaestchen laesst sich sofort wieder ausraeumen, und die Auswahl
   * setzt es nur BEIM WAEHLEN - wer es abwaehlt, bekommt es nicht beim naechsten Zeichnen zurueck.
   * Dieselbe Bauform sieht die Spec zwei Zeilen weiter fuer FOC vor („als FOC-Kandidat
   * vorgeschlagen, aber nicht erzwungen").
   *
   * DER PREIS WIRD DABEI NICHT GENULLT, anders als beim KLICK auf M (`kennzeichnen` unten, das
   * `patchNewOrderItemFlag` woertlich folgt). Der Klick ist eine Handlung des Nutzers, der Vorschlag
   * eine Beobachtung ueber den Artikel - er darf den EK aus JTL nicht ueberschreiben. Bei einem
   * X-Artikel steht dort ohnehin 0; meldete JTL etwas anderes, waere das eine Auskunft und kein
   * Rundungsfehler.
   */
  function uebernehmen(index: number, treffer: JtlArtikelTreffer): void {
    const naechste = positionen.map((p, i) =>
      i === index
        ? {
            ...p,
            name: String(treffer.name ?? ""),
            sku: String(treffer.sku ?? ""),
            manual: istManuellerArtikel(treffer.sku),
            price: treffer.price != null ? String(treffer.price) : p.price,
            kArtikel:
              treffer.id != null && String(treffer.id) !== "" ? Number(treffer.id) : null,
            mwstProzent: treffer.mwstProzent != null ? Number(treffer.mwstProzent) : null,
            // Auftrag "hatBild vorab mitgeben" (2026-08-27): OHNE diese Zeile blieb die neu
            // uebernommene Position auf dem alten (meist `undefined`) hatBild der leeren Zeile
            // stehen - Artikelbild stellte fuer einen bekannt-bildlosen Treffer trotzdem jedes Mal
            // eine Anfrage, statt die Auskunft aus DEMSELBEN Suchtreffer zu uebernehmen.
            hatBild: treffer.hatBild as boolean | undefined,
            // Auftrag "Verknuepfungsstatus in der Anlagemaske" (2026-08-28), nach demselben Muster
            // wie hatBild direkt darueber: OHNE diese Zeile bliebe die neu uebernommene Position auf
            // dem alten (meist `undefined`) Verknuepfungsstatus der leeren Zeile stehen - die
            // Positionstabelle zeigte dann fuer einen bekannt-unverknuepften Treffer keine Warnung.
            verknuepft: treffer.verknuepft as boolean | null | undefined,
            // Nachbesserung "Automatische SKU-Verknuepfung" (Pruefung von 3588d379, Punkt 2): eine
            // vom automatischen Abgleich unverknuepft gebliebene Zeile (`jtlKeinTreffer: true`) MUSS
            // die Markierung verlieren, sobald hier - wie bei jeder anderen Zeile auch - ein Treffer
            // manuell uebernommen wird. Ohne diese Zeile blieb die Zeile trotz gesetztem `kArtikel`
            // weiter rot mit "Kein JTL-Treffer" markiert - der Widerspruch, den `JtlAutoLink.ts
            // #positionMitTreffer` fuer den automatischen Fall bereits vermeidet (dieselbe Zeile
            // dort, `jtlKeinTreffer: undefined`).
            jtlKeinTreffer: undefined,
            // Ebenso "JTL-Suche gescheitert" (Fehleranzeige Paket 2, Task 8): mit gesetztem `kArtikel` waere
            // die Marke samt Ursache ein Widerspruch (`JtlAutoLink.ts#positionMitTreffer` raeumt sie ab).
            jtlSucheFehler: undefined,
          }
        : p,
    );
    onAendern(naechste as OrderItem[]);
    setSuche(null);
  }

  function patchen(index: number, feld: "qty" | "price", wert: string): void {
    const naechste = positionen.map((p, i) => (i === index ? { ...p, [feld]: wert } : p));
    onAendern(naechste as OrderItem[]);
  }

  // Wie `patchNewOrderItemFlag` (index.html:8031-8037): JEDES der beiden Kennzeichen setzt den
  // Preis auf "0" - woertlich uebernommen, auch wenn das fuer "manual" auf den ersten Blick
  // ueberraschend wirkt.
  function kennzeichnen(index: number, feld: "manual" | "foc", checked: boolean): void {
    const naechste = positionen.map((p, i) =>
      i === index ? { ...p, [feld]: checked, ...(checked ? { price: "0" } : {}) } : p,
    );
    onAendern(naechste as OrderItem[]);
  }

  function entfernen(index: number): void {
    onAendern(positionen.filter((_, i) => i !== index));
    setSuche(null);
  }

  const summe = positionen.reduce((acc, p) => acc + positionsSumme(p), 0);

  // Statuszeile (`.new-order-excel-state`) - Fassung von `renderNewOrderExcelState` (index.html:7962-
  // 7982), siehe Kopfkommentar "DER BESTELLEXCEL-IMPORT".
  let excelStatusKlasse = "new-order-excel-state";
  let excelStatusInhalt: JSX.Element | string;
  if (excelZustand.art === "busy") {
    excelStatusInhalt = "Bestellexcel wird ausgelesen …";
  } else if (excelZustand.art === "fehler") {
    excelStatusInhalt = (
      <>
        <strong>Auslesen nicht möglich</strong>
        <small>{excelZustand.meldung}</small>
      </>
    );
  } else if (excelZustand.art === "bereit") {
    excelStatusKlasse += " ready";
    /**
     * NACHZUG (F6, Fixwelle nach der Endabnahme) - ENTSCHEIDUNG: live zaehlen, wie der Bestand
     * (`index.html:7979`: `const count = newOrderDraft.items.length`). Bis hierher fror
     * `excelZustand.anzahl` die Zahl beim Import ein - fuenf Zeilen einlesen, eine loeschen liess
     * die Statuszeile weiter "5" sagen, waehrend die Tabelle darunter nur noch vier zeigte. `n` kommt
     * jetzt aus `positionen` (Prop, live vom Elternzustand) statt aus einem beim Import kopierten
     * Schnappschuss - dieselbe Quelle, die die Tabelle selbst zeichnet (`positionen.map` unten), kann
     * also nicht auseinanderlaufen. Der Import ERSETZT die Positionen ohnehin vollstaendig
     * (`index.html:8076`, siehe Kopfkommentar) - unmittelbar nach dem Auslesen ist `n` deshalb exakt
     * `entwurf.items.length`, wie zuvor.
     */
    const n = positionen.length;
    const warnungenText = excelZustand.warnungen.length
      ? ` · ${excelZustand.warnungen.join(" · ")}`
      : "";
    excelStatusInhalt = (
      <>
        <div>
          <strong>{excelZustand.quelle || "Bestellexcel"}</strong>
          <small>
            {excelZustand.blatt} · {n} Artikelposition{n === 1 ? "" : "en"} erkannt
            {warnungenText}
          </small>
        </div>
        <div className="order-excel-ready">Ausgelesen</div>
      </>
    );
  } else {
    excelStatusInhalt = "Noch keine Bestellexcel ausgewählt. Artikel können auch manuell ergänzt werden.";
  }

  return (
    <div className="new-order-excel-editor">
      {/* `index.html:3797-3801`. */}
      <div className="new-order-excel-toolbar">
        <div>
          <strong>Bestellexcel &amp; Artikel</strong>
          <span>Excel auslesen, Positionen prüfen, bearbeiten und sortieren</span>
        </div>
        <button
          className="btn-primary"
          type="button"
          disabled={excelZustand.art === "busy"}
          onClick={excelDateiWaehlen}
        >
          <Ikone name="file-spreadsheet" />
          Bestellexcel auswählen
        </button>
        <input
          ref={excelInputRef}
          type="file"
          accept=".xlsx,.xls,.xlsm,.ods,.csv"
          aria-label="Bestellexcel"
          style={{ display: "none" }}
          onChange={(event) => {
            const datei = event.currentTarget.files?.[0];
            void excelDateiVerarbeiten(datei);
          }}
        />
      </div>
      <div className={excelStatusKlasse} aria-live="polite">
        {excelStatusInhalt}
      </div>
      <div className="new-order-items-editor">
        <div className="items-table-tools">
          <span className="items-sort-hint">
            <Ikone name="arrow-up-down" />
            Positionen am Griff ziehen
          </span>
        </div>
        <div className="items-table-scroll">
          <table
            className="items resizable-items"
            id="newOrderItemsTable"
            style={{ width: `${itemTableWidth(breiten)}px` }}
          >
            {/* `itemTableColgroup` (index.html:6799). */}
            <colgroup>
              <col style={{ width: `${ITEM_REORDER_COLUMN_WIDTH}px` }} />
              <col style={{ width: `${ITEM_IMAGE_COLUMN_WIDTH}px` }} />
              {ITEM_TABLE_COLUMNS.map((spalte) => (
                <col
                  key={spalte.key}
                  data-item-column={spalte.key}
                  style={{ width: `${itemTableColumnWidth(spalte, breiten)}px` }}
                />
              ))}
              <col style={{ width: `${ITEM_ACTION_COLUMN_WIDTH}px` }} />
            </colgroup>
            <thead>
              <tr>
                <th className="item-reorder-head" aria-label="Reihenfolge"></th>
                <th className="item-image-head" aria-label="Bild"></th>
                {ITEM_TABLE_COLUMNS.map((spalte) => (
                  <th
                    key={spalte.key}
                    className={spalte.numeric ? "num" : ""}
                    data-item-column={spalte.key}
                    title={
                      spalte.key === "type"
                        ? "M = Manual/Box (Warenwert 0) · FOC = kostenlose Überlieferung"
                        : undefined
                    }
                  >
                    {spalte.label}
                  </th>
                ))}
                <th aria-label="Aktionen"></th>
              </tr>
            </thead>
            <tbody id="newOrderItemRows">
              {positionen.map((item, index) => {
                const nr = index + 1;
                const zeilensumme = positionsSumme(item);
                const offen = suche?.zeile === index;
                const listeId = `newOrderItemTreffer-${index}`;
                /**
                 * Nur ein BEREITS gewaehlter Artikel (`kArtikel != null`) kann "nicht verknuepft"
                 * sein - eine Zeile ohne Treffer hat noch gar keinen Verknuepfungsstatus, sondern
                 * einfach keinen (`undefined`). `verknuepft === false` (nicht bloss falsy): `null`
                 * ("kein Lieferant angefragt") und `undefined` (Zeile noch unbestaetigt) sollen KEINE
                 * Warnung ausloesen, nur der explizit bekannte "nicht verknuepft"-Fall.
                 */
                const nichtVerknuepft = item.kArtikel != null && item.verknuepft === false;
                /**
                 * Auftrag "Automatische SKU-Verknuepfung" (`JtlAutoLink.ts`): der automatische
                 * Abgleich hat fuer die importierte SKU dieser Zeile KEINEN exakten Treffer in JTL
                 * gefunden - EIGENE Klasse (`item-jtl-unmatched`), NICHT `item-unlinked`, weil das ein
                 * anderer Zustand ist (Artikel bekannt, aber nicht mit dem Lieferanten verknuepft).
                 * Beide schliessen sich gegenseitig aus: `jtlKeinTreffer` steht nur bei `kArtikel ===
                 * null` (siehe `JtlAutoLink.ts#positionMitTreffer`), `nichtVerknuepft` nur bei
                 * `kArtikel != null`.
                 */
                const jtlUnbestaetigt = item.jtlKeinTreffer === true;
                /**
                 * Fehleranzeige Paket 2 (Task 8): die Suche fuer die importierte SKU ist GESCHEITERT
                 * (`JtlAutoLink.ts`, `jtlSucheFehler`) - ein anderer Zustand als "kein Treffer": JTL wurde
                 * nicht befragt oder antwortete unbrauchbar, ueber den Bestand sagt das nichts. Eigene
                 * Klasse und eigene Marke samt Ursache; schliesst `jtlKeinTreffer` aus (die Zeile bekommt
                 * nie beides) und `kArtikel != null` (`uebernehmen` raeumt es ab).
                 */
                const jtlSucheGescheitert = item.jtlSucheFehler !== undefined;
                return (
                  <tr
                    key={index}
                    className={
                      [
                        item.manual || item.foc ? "item-flagged" : null,
                        nichtVerknuepft ? "item-unlinked" : null,
                        jtlUnbestaetigt ? "item-jtl-unmatched" : null,
                        jtlSucheGescheitert ? "item-jtl-suche-gescheitert" : null,
                      ]
                        .filter(Boolean)
                        .join(" ") || undefined
                    }
                    data-item-scope="new-order"
                    data-item-index={String(index)}
                    onDragOver={(ereignis) =>
                      positionZielUeberfahren(ereignis, index, ziehenRef.current)
                    }
                    onDrop={(ereignis) => {
                      const naechste = positionAbgelegt(ereignis, positionen, ziehenRef.current);
                      if (!naechste) return;
                      onAendern(naechste);
                      // Wie `entfernen`: die Umordnung verschiebt Indizes, `suche.zeile` zeigt
                      // danach auf die FALSCHE Zeile - eine offene Trefferliste haengt sonst an
                      // einer Position, deren Text/Auswahl nicht mehr zu ihr gehoert (Pruefung von
                      // cbeb1b4, Befund 1: `entfernen` raeumt aus demselben Grund).
                      setSuche(null);
                    }}
                  >
                    <td className="item-reorder-cell">
                      {/**
                       * DRITTER RIEGEL derselben Familie (Befund der Merge-Pruefung 2026-08-19,
                       * Ruling R18): `index.html:8053` sperrt die DATEIWAHL, solange `newOrderSaving`
                       * laeuft; Task 3 (Plan 2026-08-17-bestellexcel-anlagemaske) baute den
                       * UMGEKEHRTEN Riegel - `onExcelLadenAendern`/`excelZustand.art === "busy"`
                       * sperrt das SPEICHERN, solange die Bestellexcel ausgelesen wird (Kommentar
                       * ueber `onExcelLadenAendern` oben). Hier fehlte das dritte Stueck: solange
                       * `excelZustand.art === "busy"` ist, ERSETZT `excelDateiVerarbeiten` die
                       * Positionsliste GLEICH VOLLSTAENDIG (`onAendern(entwurf.items...)`) - ein
                       * Ziehvorgang, der genau in diesem Fenster beginnt, haelt in `ziehenRef` einen
                       * Index gegen die ALTE Liste fest und legt beim Ablegen (`positionAbgelegt`)
                       * gegen die NEUE (importierte) Liste ab: stille FALSCHE Umsortierung frisch
                       * importierter Positionen, ohne jede Fehlermeldung - und genau diese Positionen
                       * gehen so nach JTL. `draggable={false}` verhindert den Griff im echten Browser
                       * gar nicht erst; die Wache in `onDragStart` UND in `onKeyDown` (Tastaturweg,
                       * eigener Einstieg, unabhaengig von `draggable`) sind die eigentliche Sperre.
                       */}
                      <button
                        className="item-drag-handle"
                        type="button"
                        draggable={excelZustand.art !== "busy"}
                        aria-label={`Position ${nr} verschieben`}
                        title="Ziehen oder mit Pfeiltasten verschieben"
                        onDragStart={(ereignis) => {
                          if (excelZustand.art === "busy") {
                            ereignis.preventDefault();
                            return;
                          }
                          positionZiehenStart(ereignis, positionen, index, ziehenRef.current);
                        }}
                        onDragEnd={(ereignis) => positionZiehenEnde(ereignis, ziehenRef.current)}
                        onKeyDown={(ereignis) => {
                          if (excelZustand.art === "busy") return;
                          const ergebnis = positionTaste(ereignis, positionen, index);
                          if (!ergebnis) return;
                          onAendern(ergebnis.positionen);
                          // Siehe `onDrop` oben - derselbe Grund, derselbe Griff.
                          setSuche(null);
                          positionZielFokussieren(ergebnis.fokusIndex);
                        }}
                      >
                        <Ikone name="grip-vertical" />
                      </button>
                    </td>
                    <td className="item-image-cell">
                      <Artikelbild kArtikel={item.kArtikel} name={item.name} hatBild={item.hatBild} />
                    </td>
                    <td className="item-article-cell">
                      <input
                        value={offen ? suche.text : String(item.name ?? "")}
                        placeholder="Artikelname"
                        aria-label={`Artikel Position ${nr}`}
                        role="combobox"
                        aria-expanded={Boolean(offen && suche.liste.length)}
                        aria-controls={listeId}
                        aria-autocomplete="list"
                        autoComplete="off"
                        onChange={(event) => suchen(index, event.currentTarget.value)}
                      />
                      {nichtVerknuepft ? (
                        <span title="Nicht mit dem gewählten Lieferanten verknüpft - kann nicht bestellt werden.">
                          {verknuepftMarke(item.verknuepft)}
                        </span>
                      ) : null}
                      {jtlUnbestaetigt ? (
                        <span
                          className="jtl-treffer-marke nicht-verknuepft"
                          title="Kein Treffer in JTL zu dieser SKU."
                        >
                          Kein JTL-Treffer
                        </span>
                      ) : null}
                      {item.jtlSucheFehler !== undefined ? (
                        <>
                          <span
                            className="jtl-treffer-marke nicht-verknuepft"
                            title="Die JTL-Suche zu dieser SKU ist fehlgeschlagen – ob JTL den Artikel kennt, ist unbekannt. Die Zeile kann von Hand verknüpft werden."
                          >
                            JTL-Suche gescheitert
                          </span>
                          {item.jtlSucheFehler.ursache !== null ? (
                            <span className="jtl-suche-ursache">{item.jtlSucheFehler.ursache}</span>
                          ) : null}
                        </>
                      ) : null}
                      {offen && suche.liste.length ? (
                        <div className="jtl-treffer" id={listeId} role="listbox">
                          {suche.liste.map((artikel, k) => (
                            <button
                              key={String(artikel.id ?? k)}
                              className="jtl-treffer-zeile"
                              type="button"
                              role="option"
                              aria-selected={false}
                              // Verhindert, dass ein Fokuswechsel den Klick ueberholt.
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => uebernehmen(index, artikel)}
                            >
                              <Artikelbild
                                kArtikel={artikelKennung(artikel)}
                                name={artikel.name}
                                klickbar={false}
                                hatBild={artikel.hatBild as boolean | undefined}
                              />
                              <span className="jtl-treffer-zeile-inhalt">
                                {String(artikel.name ?? "")}
                                <span className="mono">{String(artikel.sku ?? "")}</span>
                                {verknuepftMarke(artikel.verknuepft as boolean | null | undefined)}
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : null}
                      {offen && suche.fehler ? (
                        <p className="suchfeld-hinweis" role="alert">
                          {suche.fehler}
                        </p>
                      ) : null}
                    </td>
                    <td>
                      {/* Verhaltensaenderung 3: die SKU kommt aus JTL, nicht aus der Tastatur. */}
                      <input
                        className="mono"
                        style={{ fontSize: ".78rem" }}
                        value={String(item.sku ?? "")}
                        placeholder="SKU"
                        aria-label={`SKU Position ${nr}`}
                        readOnly
                        aria-readonly="true"
                      />
                    </td>
                    <td className="num">
                      <input
                        className="qty"
                        type="number"
                        min={0}
                        placeholder="0"
                        aria-label={`Menge Position ${nr}`}
                        value={String(item.qty ?? "")}
                        onChange={(event) => patchen(index, "qty", event.target.value)}
                      />
                    </td>
                    <td className="num">
                      <input
                        className="price"
                        type="number"
                        min={0}
                        step="0.001"
                        placeholder="0,00"
                        aria-label={`EK / Stk. Position ${nr}`}
                        value={String(item.price ?? "")}
                        onChange={(event) => patchen(index, "price", event.target.value)}
                      />
                    </td>
                    <td className="row-sum">{zeilensumme ? fmtMoney({ waehrung }, zeilensumme, null) : "–"}</td>
                    <td className="item-flags">
                      <label className="iflag" title="Manual / Box – Warenwert 0">
                        <input
                          type="checkbox"
                          aria-label={`Manual / Box Position ${nr}`}
                          checked={Boolean(item.manual)}
                          onChange={(event) => kennzeichnen(index, "manual", event.target.checked)}
                        />
                        <span>M</span>
                      </label>
                      <label className="iflag foc" title="Kostenlose Überlieferung (FOC)">
                        <input
                          type="checkbox"
                          aria-label={`FOC Position ${nr}`}
                          checked={Boolean(item.foc)}
                          onChange={(event) => kennzeichnen(index, "foc", event.target.checked)}
                        />
                        <span>FOC</span>
                      </label>
                    </td>
                    <td>
                      <button
                        className="row-del"
                        type="button"
                        title="Zeile löschen"
                        onClick={() => entfernen(index)}
                      >
                        <Ikone name="trash-2" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Der Bestand blendet die Tabelle NICHT aus, er setzt den Hinweis darunter (`:8014`). */}
        {positionen.length === 0 ? (
          <div className="new-order-items-empty">Noch keine Artikel vorhanden.</div>
        ) : null}
        <button className="btn-ghost" type="button" onClick={hinzufuegen}>
          <Ikone name="circle-plus" />
          Artikel hinzufügen
        </button>
        <div className="items-total">
          <span className="label">Warenwert (Positionen)</span>
          <span className="value">{fmtMoney({ waehrung }, summe, null)}</span>
        </div>
      </div>
    </div>
  );
}
